/**
 * Status and indicator badges for License Management pages.
 *
 * Extracted from LicenseManagementPage.tsx (Sub-PR 6.3).
 */

import { Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import type { LicenseType, LicenseStatus, LicenseCriteriaStatus } from '../../types'

export function LicenseStatusBadge({
  status,
  isValid,
}: {
  status: LicenseStatus
  isValid: boolean
}): React.ReactElement {
  const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'

  if (status === 'active' && !isValid) {
    return (
      <span
        className={`${baseClasses} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400`}
      >
        Expired
      </span>
    )
  }

  const colorClasses: Record<LicenseStatus, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    suspended: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    expired: 'bg-stone-100 text-stone-800 dark:bg-stone-700 dark:text-stone-300',
    // The backend has always been able to return these two; the frontend union omitted them, so
    // `colorClasses[status]` resolved to undefined and the badge rendered unstyled.
    superseded: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    revoked: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  }

  return (
    <span className={`${baseClasses} ${colorClasses[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

export function CriteriaStatusBadge({
  status,
}: {
  status: LicenseCriteriaStatus
}): React.ReactElement {
  const baseClasses =
    'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium'

  const config: Record<LicenseCriteriaStatus, { color: string; icon: React.ElementType }> = {
    pending: {
      color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      icon: Clock,
    },
    verified: {
      color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      icon: CheckCircle,
    },
    invalid: {
      color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      icon: XCircle,
    },
    expired: {
      color: 'bg-stone-100 text-stone-800 dark:bg-stone-700 dark:text-stone-300',
      icon: AlertCircle,
    },
    used: {
      color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      icon: CheckCircle,
    },
  }

  const { color, icon: Icon } = config[status]

  return (
    <span className={`${baseClasses} ${color}`}>
      <Icon className="h-3 w-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

export function TypeBadge({ licenseType }: { licenseType: LicenseType }): React.ReactElement {
  const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'

  const colorClasses: Record<LicenseType, string> = {
    trial: 'bg-stone-100 text-stone-700 dark:bg-stone-700 dark:text-stone-300',
    professional: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    enterprise: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  }

  return (
    <span className={`${baseClasses} ${colorClasses[licenseType]}`}>
      {licenseType.charAt(0).toUpperCase() + licenseType.slice(1)}
    </span>
  )
}

export function AccessTierBadge({ tier }: { tier: string }): React.ReactElement {
  const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'

  const colorClasses: Record<string, string> = {
    basic: 'bg-stone-100 text-stone-700 dark:bg-stone-700 dark:text-stone-300',
    professional: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    enterprise: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  }

  return (
    <span className={`${baseClasses} ${colorClasses[tier] ?? colorClasses.basic ?? ''}`}>
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </span>
  )
}

const HIGH_USAGE_PERCENTAGE = 80
const PERCENTAGE_MULTIPLIER = 100

export function SeatsIndicator({
  used,
  total,
}: {
  used: number
  total: number
}): React.ReactElement {
  const percentage = total > 0 ? Math.round((used / total) * PERCENTAGE_MULTIPLIER) : 0
  const isHigh = percentage >= HIGH_USAGE_PERCENTAGE

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 max-w-24 flex-1 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-700">
        <div
          className={`h-full rounded-full ${isHigh ? 'bg-amber-500' : 'bg-[#4e5e82]'}`}
          style={{ width: `${String(percentage)}%` }}
        />
      </div>
      <span className="text-xs whitespace-nowrap text-stone-600 dark:text-stone-400">
        {used}/{total}
      </span>
    </div>
  )
}
